import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable} from 'firebase/functions';
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight, ShoppingCart } from 'lucide-react';
import { CartManager } from '../utils/CartManager';
import { getAvailableGiftCards, searchGiftCards } from '../firebase/giftcards';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';
import SeoHead from './SeoHead';
import { getBrandLandingPage } from '../data/seoLandingPages';
import { getBrandLogo } from '../utils/brandLogos';
import { formatPrice } from '../utils/formatPrice';

const getGuestSessionId = () => {
  let guestId = localStorage.getItem('swapify_guest_id');
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('swapify_guest_id', guestId);
  }
  return guestId;
};

const getDiscountPercentage = (originalValue, sellingPrice) => {
  const original = Number(originalValue || 0);
  const price = Number(sellingPrice || 0);

  if (original <= 0 || Number.isNaN(original) || Number.isNaN(price)) {
    return 0;
  }

  return Math.max(0, Math.round(((original - price) / original) * 100));
};

const BRAND_CARD_STYLES = {
  'Albert Heijn': 'from-sky-50 via-white to-blue-100 border-sky-100',
  'Bol.com': 'from-blue-50 via-white to-sky-100 border-blue-100',
  'Coolblue': 'from-cyan-50 via-white to-sky-100 border-cyan-100',
  'Douglas': 'from-pink-50 via-white to-rose-100 border-pink-100',
};

const BrandModalCard = ({ brand, card }) => (
  <div
    className={`aspect-[16/10] rounded-xl border bg-gradient-to-br p-5 sm:p-6 shadow-[0_22px_50px_-28px_rgba(15,23,42,0.28)] ${
      BRAND_CARD_STYLES[brand] || 'from-orange-50 via-white to-rose-100 border-orange-100'
    }`}
  >
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between">
        <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-xl bg-white/80 backdrop-blur border border-white/70 flex items-center justify-center overflow-hidden shadow-sm">
          <img
            src={card.logo}
            alt={`${brand} logo`}
            width="80"
            height="80"
            className="w-full h-full object-contain p-3"
            onError={(e) => {
              const firstLetter = brand.charAt(0).toUpperCase();
              e.target.src = `https://ui-avatars.com/api/?name=${firstLetter}&background=f97316&color=fff&size=80&bold=true`;
            }}
          />
        </div>
        <div className="rounded-full bg-white/80 px-3 py-1.5 text-xs sm:text-sm font-semibold text-orange-700 shadow-sm backdrop-blur">
          -{card.discount}% korting
        </div>
      </div>

      <div className="mt-auto flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-gray-500">Bonwaarde</div>
          <div className="mt-2 text-2xl sm:text-3xl font-semibold text-gray-900">
            {formatPrice(card.value)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-gray-500">Vanaf</div>
          <div className="mt-2 text-xl sm:text-2xl font-semibold text-gray-900">
            {formatPrice(card.price)}
          </div>
          <div className="mt-1 text-sm sm:text-base font-semibold text-orange-700">
            Bespaar {formatPrice(card.value - card.price)}
          </div>
        </div>
      </div>
    </div>
  </div>
);


const SwapifyMarketplace = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrandFilter, setSelectedBrandFilter] = useState(searchParams.get('brand') || '');
  const [priceRange, setPriceRange] = useState([0, 150]);
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(new Set());
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [recentlyAddedCardId, setRecentlyAddedCardId] = useState(null);
  const [toast, setToast] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [carouselAnimation, setCarouselAnimation] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const carouselTimeoutRef = useRef(null);
  const carouselAnimationFrameRef = useRef(null);
  const recentlyAddedTimeoutRef = useRef(null);
  const dragStartXRef = useRef(0);
  const dragStartYRef = useRef(0);
  const cardStackRef = useRef(null);

  
  // Real data state
  const [allCards, setAllCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'all', name: 'Alle', count: 0 },
    { id: 'shopping', name: 'Shopping', count: 0 },
    { id: 'fashion', name: 'Mode', count: 0 },
    { id: 'electronics', name: 'Electronica', count: 0 },
    { id: 'entertainment', name: 'Entertainment', count: 0 },
    { id: 'Food & Drinks', name: 'Eten & Drinken', count: 0 },
    { id: 'Beauty & Wellness', name: 'Gezondheid', count: 0 },
    { id: 'Gaming', name: 'Gaming', count: 0 },
  ];

  const syncBrandParam = useCallback((brand) => {
    const nextParams = new URLSearchParams(searchParams);
    if (brand) {
      nextParams.set('brand', brand);
    } else {
      nextParams.delete('brand');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadGiftCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filters = {};
      const cards = await getAvailableGiftCards(filters);
      
      // Transform Firebase data to match our component structure
      const transformedCards = cards.map(card => ({
        id: card.id,
        brand: card.brand,
        category: card.category || 'shopping',
        value: card.cardValue || card.originalValue,
        price: card.sellingPrice,
        discount: card.discount ?? getDiscountPercentage(card.originalValue || card.cardValue, card.sellingPrice),
        logo: getBrandLogo(card.brand),
        popular: card.popular || false,
        views: card.views || 0,
        createdAt: card.createdAt,
        sellerId: card.sellerId,
        sellerName: card.sellerName
      }));
      
      setAllCards(transformedCards);
    } catch (error) {
      console.error('Error loading gift cards:', error);
      setError('Kon cadeaubonnen niet laden. Probeer de pagina te vernieuwen.');
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps


  // Handle search with Firebase
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // If empty search, reload all cards
      loadGiftCards();
      return;
    }
    
    setLoading(true);
    try {
      const results = await searchGiftCards(query);
      const transformedResults = results.map(card => ({
        id: card.id,
        brand: card.brand,
        category: card.category || 'shopping',
        value: card.cardValue || card.originalValue,
        price: card.sellingPrice,
        discount: card.discount ?? getDiscountPercentage(card.originalValue || card.cardValue, card.sellingPrice),
        logo: getBrandLogo(card.brand),
        popular: card.popular || false,
        views: card.views || 0,
        createdAt: card.createdAt,
        sellerId: card.sellerId,
        sellerName: card.sellerName
      }));
      setAllCards(transformedResults);
    } catch (error) {
      console.error('Error searching gift cards:', error);
      setError('Zoeken mislukt. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  }, [loadGiftCards]); // Depends on loadGiftCards


 // Update useEffect for initial load
useEffect(() => {
  loadGiftCards();
}, [loadGiftCards]);

useEffect(() => {
  const brandParam = searchParams.get('brand') || '';
  setSelectedBrandFilter(brandParam);
}, [searchParams]);

useEffect(() => {
  if (toast) {
    setToastVisible(false);
    const showTimer = requestAnimationFrame(() => setToastVisible(true));
    const hideTimer = setTimeout(() => setToastVisible(false), 4000);
    const removeTimer = setTimeout(() => setToast(null), 4250);

    return () => {
      cancelAnimationFrame(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }
}, [toast]);

// Update useEffect for search
useEffect(() => {
  const timeoutId = setTimeout(() => {
    handleSearch(searchQuery);
  }, 500);
  
  return () => clearTimeout(timeoutId);
}, [searchQuery, handleSearch]);

useEffect(() => {
  const sync = () => {
    const { items = [] } = JSON.parse(localStorage.getItem('swapify_cart') || '{"items":[]}');
    // Your CartManager stores items with unique cartIds, so match by original giftCard id
    setAddedToCart(new Set(items.map(i => i.id)));
  };
  sync();
}, [selectedBrand, carouselIndex]); // Runs every time brand changes or user swipes cards

useEffect(() => () => {
  if (carouselTimeoutRef.current) {
    clearTimeout(carouselTimeoutRef.current);
  }
  if (carouselAnimationFrameRef.current) {
    cancelAnimationFrame(carouselAnimationFrameRef.current);
  }
  if (recentlyAddedTimeoutRef.current) {
    clearTimeout(recentlyAddedTimeoutRef.current);
  }
}, []);

  // Group cards by brand
  const groupedCards = allCards.reduce((acc, card) => {
    if (!acc[card.brand]) {
      acc[card.brand] = [];
    }
    acc[card.brand].push(card);
    return acc;
  }, {});

  // Sort each brand's cards by lowest price first
  Object.keys(groupedCards).forEach(brand => {
    groupedCards[brand].sort((a, b) => a.price - b.price);
  });

  // Create display cards (one per brand with lowest price)
  const displayCards = Object.keys(groupedCards).map(brand => {
    const brandCards = groupedCards[brand];
    const lowestPriceCard = brandCards[0];
    const count = brandCards.length;
    
    return {
      ...lowestPriceCard,
      count: count,
      allCards: brandCards
    };
  });

  const availableBrands = Object.keys(groupedCards).sort((a, b) => a.localeCompare(b, 'nl'));
  const activeBrandFilter = (
    availableBrands.includes(selectedBrandFilter) || getBrandLandingPage(selectedBrandFilter)
  ) ? selectedBrandFilter : '';
  const visibleBrandOptions = activeBrandFilter && !availableBrands.includes(activeBrandFilter) ?
    [activeBrandFilter, ...availableBrands] :
    availableBrands;

  // Filter display cards
  const filteredCards = displayCards
    .filter(card => {
      const matchesCategory = selectedCategory === 'all' || card.category === selectedCategory;
      const matchesBrand = !activeBrandFilter || card.brand === activeBrandFilter;
      const matchesPrice = card.price >= priceRange[0] && card.price <= priceRange[1];
      return matchesCategory && matchesBrand && matchesPrice;
    })
    .sort((a, b) => {
      if (sortBy === 'popular') return (b.popular ? 1 : 0) - (a.popular ? 1 : 0);
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'discount') return b.discount - a.discount;
      if (sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      return 0;
    });

  const matchingBrandLanding = getBrandLandingPage(activeBrandFilter);
  const canonicalPath = matchingBrandLanding ? matchingBrandLanding.path : '/marketplace';
  const hasOtherActiveFilters = selectedCategory !== 'all' ||
    priceRange[0] !== 0 ||
    priceRange[1] !== 150 ||
    searchQuery.trim() !== '';
  const seoTitle = activeBrandFilter ?
    `${activeBrandFilter} cadeaukaarten met korting | Swapify marktplaats` :
    'Cadeaukaarten met korting | Swapify marktplaats';
  const seoDescription = activeBrandFilter ?
    `Bekijk beschikbare ${activeBrandFilter} cadeaukaarten op de Swapify marktplaats. Veilig via iDEAL en code na verificatie per e-mail.` :
    'Koop cadeaukaarten met korting van merken als Albert Heijn, Bol.com, Coolblue en Douglas. Veilig via iDEAL en code na verificatie per e-mail.';

  // Update category counts
  categories.forEach(cat => {
    if (cat.id === 'all') {
      cat.count = displayCards.length;
    } else {
      cat.count = displayCards.filter(c => c.category === cat.id).length;
    }
  });

  const openBrandModal = (brand) => {
    setSelectedBrand(brand);
    setCarouselIndex(0);
    setCarouselAnimation(null);
    setDragOffset(0);
    setIsDragging(false);
  };

  const closeBrandModal = () => {
    if (carouselTimeoutRef.current) {
      clearTimeout(carouselTimeoutRef.current);
      carouselTimeoutRef.current = null;
    }
    if (carouselAnimationFrameRef.current) {
      cancelAnimationFrame(carouselAnimationFrameRef.current);
      carouselAnimationFrameRef.current = null;
    }
    setSelectedBrand(null);
    setCarouselIndex(0);
    setCarouselAnimation(null);
    setDragOffset(0);
    setIsDragging(false);
  };

  const animateToCard = (targetIndex, directionOverride = null) => {
    if (!selectedBrand || carouselAnimation || targetIndex === carouselIndex) return;
    if (targetIndex < 0 || targetIndex >= selectedBrand.allCards.length) return;

    const direction = directionOverride || (targetIndex > carouselIndex ? 'next' : 'prev');
    setCarouselAnimation({ direction, targetIndex, phase: 'start' });
    setIsDragging(false);
    setDragOffset(0);

    carouselAnimationFrameRef.current = requestAnimationFrame(() => {
      setCarouselAnimation((current) => current ? { ...current, phase: 'active' } : current);
      carouselAnimationFrameRef.current = null;
    });

    carouselTimeoutRef.current = setTimeout(() => {
      setCarouselIndex(targetIndex);
      setCarouselAnimation(null);
      carouselTimeoutRef.current = null;
    }, 220);
  };

  const nextCard = () => {
    if (selectedBrand && carouselIndex < selectedBrand.allCards.length - 1) {
      animateToCard(carouselIndex + 1);
    }
  };

  const prevCard = () => {
    if (carouselIndex > 0) {
      animateToCard(carouselIndex - 1);
    }
  };

 const handleAddToCart = async (giftCard) => {
  setIsAdding(true);
  setError(null);
  
  try {
    const functions = getFunctions(undefined, 'europe-west1');
    const reserveCard = httpsCallable(functions, 'reserveCard');

      // Get guest session ID if not logged in
    const auth = getAuth();
    const payload = { cardId: giftCard.id };
    if (!auth.currentUser) {
      payload.guestSessionId = getGuestSessionId();
    }
    
    const result = await reserveCard(payload);

    if (result.data.success) {
      // Make sure to use consistent property names
      const cartItem = {
        id: giftCard.id,
        brand: giftCard.brand,
        value: giftCard.value,        
        price: giftCard.price,        
        discount: giftCard.discount,
        logo: giftCard.logo,
        cartId: `${giftCard.id}_${Date.now()}`,
        reservedUntil: result.data.reservedUntil,
        reservedBy: result.data.reservedBy,
        addedAt: new Date().toISOString()
      };
      
      CartManager.addToCart(cartItem);
      setAddedToCart(prev => new Set([...prev, giftCard.id]));
      setRecentlyAddedCardId(giftCard.id);
      if (recentlyAddedTimeoutRef.current) {
        clearTimeout(recentlyAddedTimeoutRef.current);
      }
      recentlyAddedTimeoutRef.current = setTimeout(() => {
        setRecentlyAddedCardId((current) => current === giftCard.id ? null : current);
      }, 1800);
      setToast({ brand: giftCard.brand });
      loadGiftCards();
    }

    

  } catch (error) {
    console.error("Error adding to cart:", error);
    let errorMessage = "Kon niet toevoegen aan winkelwagen";
    
    if (error.code === "functions/failed-precondition") {
      errorMessage = error.message;
    } else if (error.code === "functions/not-found") {
      errorMessage = "Cadeaubon niet gevonden";
    }
    
    setError(errorMessage);
    alert(errorMessage);
  } finally {
    setIsAdding(false);
  }
};


  // Swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    if (carouselAnimation || !selectedBrand || selectedBrand.allCards.length <= 1) return;
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
    dragStartXRef.current = e.targetTouches[0].clientX;
    dragStartYRef.current = e.targetTouches[0].clientY;
    setIsDragging(true);
    setDragOffset(0);
  };

  const onTouchMove = (e) => {
    if (!isDragging || carouselAnimation) return;
    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    const deltaX = currentX - dragStartXRef.current;
    const deltaY = currentY - dragStartYRef.current;

    setTouchEnd(currentX);
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
      setDragOffset(deltaX);
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    if (carouselAnimation) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    const stackWidth = cardStackRef.current?.offsetWidth || 0;
    const swipeThreshold = stackWidth * 0.2;
    const absoluteDragOffset = Math.abs(dragOffset);

    if (absoluteDragOffset > swipeThreshold) {
      if (dragOffset < 0 && selectedBrand && carouselIndex < selectedBrand.allCards.length - 1) {
        animateToCard(carouselIndex + 1, 'next');
      } else if (dragOffset > 0 && carouselIndex > 0) {
        animateToCard(carouselIndex - 1, 'prev');
      } else {
        setDragOffset(0);
        setIsDragging(false);
      }
    } else if (isLeftSwipe && selectedBrand && carouselIndex < selectedBrand.allCards.length - 1 && absoluteDragOffset > minSwipeDistance) {
      animateToCard(carouselIndex + 1, 'next');
    } else if (isRightSwipe && carouselIndex > 0 && absoluteDragOffset > minSwipeDistance) {
      animateToCard(carouselIndex - 1, 'prev');
    } else {
      setDragOffset(0);
      setIsDragging(false);
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleToastClick = () => {
    setToastVisible(false);
    setToast(null);
    navigate('/buy');
  };

  // Loading state
  if (loading && allCards.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
        <SeoHead
          title={seoTitle}
          description={seoDescription}
          canonicalPath={canonicalPath}
        />
        {/* Navigation */}
        <Navbar />

        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Cadeaubonnen laden...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
        <SeoHead
          title={seoTitle}
          description={seoDescription}
          canonicalPath={canonicalPath}
        />
        <Navbar />

        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Oeps, er ging iets mis</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button 
              onClick={loadGiftCards}
              className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition"
            >
              Opnieuw proberen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
      />
      <Navbar />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">Marktplaats</h1>
          <p className="text-gray-600">
            {activeBrandFilter ?
              `Je bekijkt nu het aanbod voor ${activeBrandFilter}.` :
              'Bekijk cadeaukaarten van winkels die je echt gebruikt. Met korting, zonder gedoe.'}
          </p>
          
          {/* Search and Filter Bar */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Zoek op merk"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-lg border-2 border-gray-200 focus:border-red-500 focus:outline-none"
              />
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-red-500 transition"
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span>Filter</span>
            </button>
            <select
              value={activeBrandFilter}
              onChange={(e) => {
                const nextBrand = e.target.value;
                setSelectedBrandFilter(nextBrand);
                syncBrandParam(nextBrand);
              }}
              className="px-6 py-3 bg-white border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
            >
              <option value="">Alle merken</option>
              {visibleBrandOptions.map((brand) => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-6 py-3 bg-white border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
            >
              <option value="popular">Meest gekozen</option>
              <option value="newest">Nieuw toegevoegd</option>
              <option value="price-low">Prijs: Laag - Hoog</option>
              <option value="price-high">Prijs: Hoog - Laag</option>
              <option value="discount">Hoogste korting</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Categorieën</h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{category.name}</span>
                      <span className={`text-sm ${
                        selectedCategory === category.id ? 'text-white' : 'text-gray-400'
                      }`}>
                        {category.count}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Prijsbereik</h3>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{formatPrice(priceRange[0])}</span>
                    <span>{formatPrice(priceRange[1])}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="150"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Filters - (same as before) */}
          {showFilters && (
            <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
              <div className="bg-white rounded-t-2xl w-full max-h-[80vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Filter</h3>
                    <button onClick={() => setShowFilters(false)}>
                      <X className="w-6 h-6 text-gray-400" />
                    </button>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Categorieën</h4>
                    <div className="space-y-2">
                      {categories.map(category => (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition ${
                            selectedCategory === category.id
                              ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span>{category.name}</span>
                            <span className={`text-sm ${
                              selectedCategory === category.id ? 'text-white' : 'text-gray-400'
                            }`}>
                              {category.count}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Merk</h4>
                    <select
                      value={activeBrandFilter}
                      onChange={(e) => {
                        const nextBrand = e.target.value;
                        setSelectedBrandFilter(nextBrand);
                        syncBrandParam(nextBrand);
                      }}
                      className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Alle merken</option>
                      {visibleBrandOptions.map((brand) => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Prijsbereik</h4>
                    <div className="space-y-4">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{formatPrice(priceRange[0])}</span>
                        <span>{formatPrice(priceRange[1])}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="150"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-lg font-semibold"
                  >
                    Toon {filteredCards.length} resultaten
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Gift Cards Grid */}
          <div className="flex-1">
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {activeBrandFilter ? `${filteredCards.length} resultaten voor ${activeBrandFilter}` : `${filteredCards.length} merken in het aanbod`}
              </div>
              {loading && (
                <div className="text-sm text-gray-500">Aan het laden...</div>
              )}
            </div>
            {activeBrandFilter ? (
              <div className="mb-4">
                <button
                  onClick={() => {
                    setSelectedBrandFilter('');
                    syncBrandParam('');
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-200"
                >
                  Merkfilter: {activeBrandFilter}
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            ) : null}
            
            {filteredCards.length === 0 && !loading ? (
              <div className="text-center py-16">
                {activeBrandFilter ? (
                  <>
                    <p className="text-gray-700 text-xl font-semibold">Jammer, op dit moment zijn er geen {activeBrandFilter} cadeaubonnen beschikbaar</p>
                    <p className="text-gray-500 mt-3 max-w-xl mx-auto">Kijk later nog eens, er komen regelmatig nieuwe bonnen bij. Of neem meteen een kijkje bij andere merken.</p>
                    <Link
                      to="/marketplace"
                      onClick={() => {
                        setSelectedBrandFilter('');
                        syncBrandParam('');
                      }}
                      className="inline-flex items-center justify-center mt-6 rounded-xl border border-orange-200 px-5 py-3 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
                    >
                      Bekijk alle cadeaukaarten
                    </Link>
                  </>
                ) : displayCards.length === 0 && !hasOtherActiveFilters ? (
                  <>
                    <p className="text-gray-700 text-xl font-semibold">Er zijn momenteel geen cadeaukaarten beschikbaar</p>
                    <p className="text-gray-500 mt-3 max-w-xl mx-auto">We zijn net begonnen. Binnenkort vind je hier cadeaukaarten met korting.</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-500 text-lg">Geen cadeaukaarten gevonden</p>
                    <p className="text-gray-400 mt-2">Pas je filters aan of neem een kijkje bij een ander merk.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                {filteredCards.map((card) => (
                  <div 
                    key={`${card.brand}-${card.id}`} 
                    onClick={() => openBrandModal(card)}
                    
                    className="bg-white rounded-xl p-3 sm:p-5 shadow-sm hover:shadow-lg transition cursor-pointer group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        <img 
                          src={card.logo} 
                          alt={`${card.brand} logo`}
                          width="80"
                          height="80"
                          className="w-full h-full object-contain p-2"
                          onError={(e) => {
                            if (!card) return;
                            const firstLetter = card.brand.charAt(0).toUpperCase();
                            e.target.src = `https://ui-avatars.com/api/?name=${firstLetter}&background=f97316&color=fff&size=80&bold=true`;
                          }}
                        />
                      </div>
                      <div className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                        -{card.discount}%
                      </div>
                    </div>
                    <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 truncate">{card.brand}</h3>
                    <div className="flex items-baseline space-x-2 mb-2">
                      <span className="text-lg sm:text-2xl font-bold text-gray-900">{formatPrice(card.price)}</span>
                      <span className="text-sm text-gray-500 line-through">{formatPrice(card.value)}</span>
                    </div>
                    <div className="hidden sm:block text-sm text-gray-600 mb-3">
                      {card.count > 1 ? `${card.count} beschikbaar vanaf ${formatPrice(card.price)}` : '1 beschikbaar'}
                    </div>
                    <button className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition hover:shadow-lg hover:-translate-y-0.5">

                      Bekijk aanbod
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Brand Modal with Cart Functionality */}
      {selectedBrand && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeBrandModal}
          style={{ touchAction: 'pan-y' }}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white px-6 py-5 border-b border-gray-100 flex justify-between items-start gap-4">
              <div>
                <h2 className="text-lg sm:text-2xl font-semibold text-gray-900">{selectedBrand.brand} cadeaukaarten</h2>
                <p className="text-sm text-gray-600 mt-1">{selectedBrand.allCards.length} opties beschikbaar</p>
              </div>
              <button 
                onClick={closeBrandModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {(() => {
                const cards = selectedBrand.allCards;
                const hasStack = cards.length > 1;
                const frontIndex = carouselAnimation ? carouselAnimation.targetIndex : carouselIndex;
                const activeCard = cards[frontIndex];
                const stackWidth = cardStackRef.current?.offsetWidth || 1;
                const dragProgress = hasStack ? Math.min(Math.abs(dragOffset) / stackWidth, 1) : 0;
                const dragRotation = Math.max(-3, Math.min(3, dragOffset / 35));
                const stackIndexes = hasStack ? Array.from(new Set([
                  frontIndex,
                  frontIndex + 1,
                  frontIndex + 2,
                  carouselAnimation ? carouselIndex : null,
                ].filter((index) => index != null && index >= 0 && index < cards.length))) : [carouselIndex];

                const getCardPresentation = (index) => {
                  if (!hasStack) {
                    return {
                      zIndex: 30,
                      opacity: 1,
                      transform: 'translate3d(0, 0, 0) scale(1)',
                      pointerEvents: 'auto',
                      transition: 'transform 200ms ease-out',
                    };
                  }

                  if (carouselAnimation && index === carouselIndex) {
                    return {
                      zIndex: 30,
                      opacity: 1,
                      transform: carouselAnimation.phase === 'start'
                        ? 'translate3d(0, 0, 0) rotate(0deg) scale(1)'
                        : carouselAnimation.direction === 'next'
                          ? 'translate3d(-120%, 0, 0) rotate(-7deg) scale(1)'
                          : 'translate3d(120%, 0, 0) rotate(7deg) scale(1)',
                      pointerEvents: 'none',
                      transition: 'transform 200ms ease-out',
                    };
                  }

                  if (isDragging && index === carouselIndex) {
                    return {
                      zIndex: 30,
                      opacity: 1,
                      transform: `translate3d(${dragOffset}px, 0, 0) rotate(${dragRotation}deg) scale(1)`,
                      pointerEvents: 'auto',
                      transition: 'none',
                    };
                  }

                  if (index === frontIndex) {
                    const incomingFromLeft = carouselAnimation && carouselAnimation.direction === 'prev' && index === carouselAnimation.targetIndex;
                    const incomingFromRight = carouselAnimation && carouselAnimation.direction === 'next' && index === carouselAnimation.targetIndex;

                    return {
                      zIndex: 20,
                      opacity: 1,
                      transform: carouselAnimation && carouselAnimation.phase === 'start'
                        ? incomingFromLeft
                          ? 'translate3d(-120%, 0, 0) rotate(-7deg) scale(1)'
                          : incomingFromRight
                            ? 'translate3d(18px, 14px, 0) scale(0.95)'
                            : 'translate3d(0, 0, 0) scale(1)'
                        : 'translate3d(0, 0, 0) scale(1)',
                      pointerEvents: 'auto',
                      transition: carouselAnimation
                        ? 'transform 200ms ease-out'
                        : isDragging
                          ? 'none'
                          : 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    };
                  }

                  if (index === frontIndex + 1) {
                    const scaledUp = hasStack && isDragging && dragOffset < 0 && index === carouselIndex + 1;
                    return {
                      zIndex: 10,
                      opacity: 0.72,
                      transform: `translate3d(18px, 14px, 0) scale(${scaledUp ? 0.95 + (dragProgress * 0.03) : 0.95})`,
                      pointerEvents: 'none',
                      transition: carouselAnimation ? 'transform 200ms ease-out' : 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    };
                  }

                  if (index === frontIndex + 2) {
                    return {
                      zIndex: 5,
                      opacity: 0.55,
                      transform: 'translate3d(34px, 28px, 0) scale(0.91)',
                      pointerEvents: 'none',
                      transition: carouselAnimation ? 'transform 200ms ease-out' : 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    };
                  }

                  return {
                    zIndex: 1,
                    opacity: 0,
                      transform: 'translate3d(0, 0, 0) scale(0.9)',
                    pointerEvents: 'none',
                    transition: 'transform 200ms ease-out',
                  };
                };

	                return (
	                  <>
                    <div
                      ref={cardStackRef}
                      className="relative select-none overflow-hidden"
                      onTouchStart={onTouchStart}
                      onTouchMove={onTouchMove}
                      onTouchEnd={onTouchEnd}
                      style={{ touchAction: 'pan-y', overscrollBehaviorX: 'contain' }}
                    >
                      <div className="grid [grid-template-areas:'stack'] pb-8 will-change-transform">
                        {stackIndexes
                          .sort((a, b) => a - b)
                          .map((index) => {
                            const presentation = getCardPresentation(index);

                            return (
                              <div
                                key={cards[index].id}
                                className="col-start-1 row-start-1"
                                style={{
                                  zIndex: presentation.zIndex,
                                  opacity: presentation.opacity,
                                  transform: presentation.transform,
                                  pointerEvents: presentation.pointerEvents,
                                  transition: presentation.transition,
                                  willChange: 'transform, opacity',
                                }}
                              >
                                <BrandModalCard
                                  brand={selectedBrand.brand}
                                  card={cards[index]}
                                />
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    {hasStack && (
                    <div className="mt-2 flex items-center justify-center gap-4">
                      <button
                        onClick={prevCard}
                        disabled={carouselIndex === 0 || Boolean(carouselAnimation)}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Vorige kaart"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      <div className="flex items-center justify-center space-x-2">
                        {cards.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => animateToCard(index)}
                            disabled={Boolean(carouselAnimation)}
                            className={`h-2 rounded-full transition ${
                              index === (carouselAnimation ? carouselAnimation.targetIndex : carouselIndex)
                                ? 'bg-red-500 w-8'
                                : 'bg-gray-300 hover:bg-gray-400 w-2'
                            }`}
                            aria-label={`Ga naar kaart ${index + 1}`}
                          />
                        ))}
                      </div>

                      <button
                        onClick={nextCard}
                        disabled={carouselIndex === cards.length - 1 || Boolean(carouselAnimation)}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Volgende kaart"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                    )}

	                    <div className="mt-6">
	                      {recentlyAddedCardId === activeCard.id ? (
	                        <button
	                          disabled
	                          className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold transition text-sm sm:text-base bg-green-600 text-white"
	                        >
	                          <span>✓ Toegevoegd</span>
	                        </button>
	                      ) : addedToCart.has(activeCard.id) ? (
	                        <div className="space-y-3">
	                          <p className="text-sm text-gray-600">Al in je winkelmandje</p>
	                          <Link
	                            to="/buy"
	                            className="w-full flex items-center justify-center px-5 py-3 rounded-xl border-2 border-red-200 text-red-600 font-semibold hover:bg-red-50 transition text-sm sm:text-base"
	                          >
	                            Naar winkelmandje →
	                          </Link>
	                        </div>
	                      ) : (
	                        <button
	                          onClick={() => handleAddToCart(activeCard)}
	                          disabled={isAdding}
	                          className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold transition text-sm sm:text-base ${
	                            isAdding
	                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
	                              : 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:shadow-lg'
	                          }`}
	                        >
	                          <ShoppingCart className="w-5 h-5" />
	                          <span>{isAdding ? 'Bezig...' : 'In winkelmandje'}</span>
	                        </button>
	                      )}
	                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        <span>Code na verificatie per e-mail</span>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        <span>Veilig betalen via iDEAL</span>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        <span>Minimaal 30 dagen geldig</span>
                      </div>
                    </div>

                    <div className="mt-8 border-t border-gray-100" />

                  </>
                );
              })()}

              {/* All Cards List with Cart Icons */}
              <div className="mt-8 pt-8">
                <h3 className="font-semibold text-gray-900 mb-4">Alle cadeaubonnen van dit merk</h3>
                <div className="space-y-3">
                  {selectedBrand.allCards.map((card, index) => (
                    <div 
                      key={card.id}
                      className={`flex items-center justify-between p-4 rounded-xl border transition ${
                        index === carouselIndex
                          ? 'border-red-200 bg-red-50/60'
                          : 'border-gray-200 hover:border-red-200'
                      }`}
                    >
                      <div className="flex-1 cursor-pointer" onClick={() => setCarouselIndex(index)}>
                        <div className="font-semibold text-gray-900">Waarde {formatPrice(card.value)}</div>
                        <div className="text-sm text-gray-600">Je voordeel: {formatPrice(card.value - card.price)}</div>
                      </div>
                      <div className="text-right mr-4">
                        <div className="text-base sm:text-lg font-semibold text-gray-900">{formatPrice(card.price)}</div>
                        <div className="text-xs text-orange-600 font-semibold">{card.discount}% korting</div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(card);
                        }}
                        disabled={isAdding || addedToCart.has(card.id)}
                        className={`p-3 rounded-lg transition ${
                          addedToCart.has(card.id)
                            ? 'bg-orange-500 text-white'
                            : isAdding
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                        title="In winkelmandje"
                      >
                        {isAdding ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : addedToCart.has(card.id) ? (
                          <span className="text-sm">✓</span>
                        ) : (
                          <ShoppingCart className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <SiteFooter />

    {toast && (
      <div className="pointer-events-none fixed bottom-4 left-4 right-4 z-50 sm:bottom-auto sm:left-auto sm:right-4 sm:top-4">
        <button
          type="button"
          onClick={handleToastClick}
          className={`pointer-events-auto flex w-full max-w-[420px] items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left shadow-[0_16px_40px_-24px_rgba(15,23,42,0.28)] transition duration-300 sm:ml-auto ${
            toastVisible
              ? 'translate-y-0 opacity-100'
              : 'translate-y-4 opacity-0 sm:-translate-y-4'
          }`}
        >
          <div className="min-w-0 text-sm text-gray-700">
            <span className="font-semibold text-emerald-600">✓</span>{' '}
            <span className="font-medium">{toast.brand} toegevoegd aan je winkelmandje</span>
          </div>
          <span className="shrink-0 text-sm font-semibold text-red-500">
            Bekijk →
          </span>
        </button>
      </div>
    )}
    </div>
    );
};


export default SwapifyMarketplace;
